/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { SignJWT, type JWTPayload, jwtVerify } from "jose";

/* -------------------------------------------------------------------------- */
/*                                 Utility Class                              */
/* -------------------------------------------------------------------------- */

type JwtOptions = {
  alg?: string;
  secret?: Uint8Array;
  expiresIn?: string;
  issuer?: string;
};

const env = {
  jwt: {
    secret: import.meta.env.VITE_JWT_SECRET,
    issuer: import.meta.env.VITE_JWT_ISSUER,
  }
}

/**
 * JWT utility class for signing and verifying JSON Web Tokens (JWTs).
 */
export class Jwt {
  private static instance: Jwt | null = null;
  private alg = "HS256";
  private secret = new TextEncoder().encode(env.jwt.secret);
  private expiresIn = "365d";
  private issuer: string = env.jwt.issuer;

  constructor(options?: JwtOptions) {
    if (Jwt.instance) {
      return Jwt.instance;
    }

    if (options) {
      if (options.alg) this.alg = options.alg;
      if (options.secret) this.secret = options.secret;
      if (options.expiresIn) this.expiresIn = options.expiresIn;
      if (options.issuer) this.issuer = options.issuer;
    }
    Jwt.instance = this;
  }

  /**
   * Signs a JWT with the provided payload and expiration time.
   * @param params - Object containing the payload and optional expiration time for the JWT.
   * @param params.payload - Payload to be included in the JWT.
   * @param params.expirationTime - Expiration time for the JWT. Defaults to the value specified in the constructor.
   * @returns A Promise that resolves to the signed JWT.
   */
  async sign({
    payload,
    expirationTime,
  }: {
    payload: JWTPayload;
    expirationTime?: string;
  }): Promise<string> {
    const jwt = await new SignJWT(payload)
      .setProtectedHeader({ alg: this.alg })
      .setIssuer(this.issuer)
      .setExpirationTime(expirationTime ?? this.expiresIn)
      .sign(this.secret);

    return jwt;
  }

  /**
   * Verifies and decodes a JWT.
   * @param params - Object containing the JWT.
   * @param params.jwt - The JWT to verify and decode.
   * @returns A Promise that resolves to the decoded payload of the JWT.
   */
  async verify({ jwt }: { jwt: string }): Promise<JWTPayload> {
    const { payload } = await jwtVerify(jwt, this.secret, {
      issuer: this.issuer,
    });
    return payload;
  }
}
